/**
 * Event deduplication utilities for PostHog
 *
 * Prevents duplicate events from React re-renders and rapid user actions.
 * Uses in-memory cache with FIFO eviction and time-window based deduplication.
 *
 * Story 4.1: PostHog Frontend Analytics
 */

import type { IAnalyticsGateway } from "@/domain/ports/IAnalyticsGateway";
import { PostHogEvents } from "./events.enum";
import { sanitizePayload } from "./payload.utils";
import type { BaseEventPayload } from "./payload.types";

interface CachedEvent {
  key: string;
  timestamp: number;
  payload: Record<string, unknown>;
}

// Module-level state
let eventCache: CachedEvent[] = [];
let analyticsGatewayRef: IAnalyticsGateway | null = null;

const MAX_CACHE_SIZE = 100;
const DEFAULT_TIME_WINDOW_MS = 500;
const CLEANUP_MULTIPLIER = 2;

// Custom time windows per event type
const EVENT_TIME_WINDOWS: Partial<Record<PostHogEvents, number>> = {
  [PostHogEvents.SETTINGS_VIEWED]: 1000,
  [PostHogEvents.LANGUAGE_CHANGED]: 2000,
  [PostHogEvents.TROPHY_ROOM_VIEWED]: 800,
  [PostHogEvents.NFC_SCAN]: 1000, // Prevent rapid scan duplicates
  [PostHogEvents.PROFILE_VIEWED]: 800,
  [PostHogEvents.PAGE_VIEWED]: 500,
};

/**
 * Initialize deduplication with analytics gateway reference
 * Must be called before using captureWithDeduplication
 */
export function initDeduplication(gateway: IAnalyticsGateway): void {
  analyticsGatewayRef = gateway;
}

/**
 * Get the time window for a specific event type
 */
export function getEventTimeWindow(eventName: string): number {
  return (
    EVENT_TIME_WINDOWS[eventName as PostHogEvents] || DEFAULT_TIME_WINDOW_MS
  );
}

/**
 * Creates a unique key for an event based on event name and critical payload fields
 */
export function createEventKey(
  eventName: string,
  payload: Record<string, unknown>
): string {
  const keyFields = ["partnerId", "productId", "path", "ownerId", "profileId"];
  const keyParts: string[] = [eventName];

  for (const field of keyFields) {
    if (payload[field] !== undefined && payload[field] !== null) {
      keyParts.push(`${field}:${String(payload[field])}`);
    }
  }

  return keyParts.join("|");
}

/**
 * Check if an event is a duplicate within the time window
 */
export function isDuplicate(
  eventKey: string,
  now: number,
  timeWindow: number
): boolean {
  const existingEvent = eventCache.find((e) => e.key === eventKey);
  if (!existingEvent) return false;
  return now - existingEvent.timestamp < timeWindow;
}

/**
 * Add event to cache with FIFO eviction and cleanup
 */
export function addToCache(event: CachedEvent): void {
  // FIFO eviction if cache is full
  if (eventCache.length >= MAX_CACHE_SIZE) {
    eventCache.shift();
  }

  // Add new event
  eventCache.push(event);

  // Cleanup old events beyond 2x time window
  cleanupCache();
}

/**
 * Remove old events from cache
 */
export function cleanupCache(): void {
  const cutoff = Date.now() - DEFAULT_TIME_WINDOW_MS * CLEANUP_MULTIPLIER;
  eventCache = eventCache.filter((e) => e.timestamp >= cutoff);
}

/**
 * Clear entire cache (useful for testing)
 */
export function clearCache(): void {
  eventCache = [];
}

/**
 * Get current cache size (useful for testing)
 */
export function getCacheSize(): number {
  return eventCache.length;
}

/**
 * Captures event with deduplication to prevent duplicates from re-renders
 *
 * @param eventName - Event name from PostHogEvents enum
 * @param payload - Event payload (timestamp removed before sending to PostHog)
 * @returns true if event was captured, false if deduplicated (or gateway not initialized)
 */
export function captureWithDeduplication(
  eventName: string,
  payload: Record<string, unknown>
): boolean {
  if (!analyticsGatewayRef) {
    console.warn("PostHog: Analytics gateway not initialized");
    return false;
  }

  const now = Date.now();
  const timeWindow = getEventTimeWindow(eventName);
  const eventKey = createEventKey(eventName, payload);

  // Check for duplicate
  if (isDuplicate(eventKey, now, timeWindow)) {
    if (import.meta.env.DEV) {
      console.log(
        `PostHog Deduplication: Skipped duplicate "${eventName}" (within ${timeWindow}ms window)`
      );
    }
    return false;
  }

  // Remove timestamp from payload before sending to PostHog
  const sanitizedPayload = sanitizePayload(payload as BaseEventPayload);

  // Capture event
  analyticsGatewayRef.capture(eventName, sanitizedPayload);

  if (import.meta.env.DEV) {
    console.log(`PostHog: ${eventName}`, sanitizedPayload);
  }

  // Update cache
  addToCache({ key: eventKey, timestamp: now, payload });

  return true;
}
